// Reusable form-field validators shared across onboarding & settings flows.
// Each returns a string error message when invalid, or null when valid.
//
// Conventions:
// - Empty/whitespace-only values are treated as "not provided" — return null
//   unless `required: true` is passed.
// - "Numeric-only or symbol-only" inputs are rejected for human-readable text
//   fields (Tagline, Address, Bio, etc.) by requiring at least one Unicode
//   letter via `\p{Letter}`.

const URL_REGEX = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SOCIAL_HANDLE_REGEX = /^@[A-Za-z0-9._-]{1,30}$/;
const PHONE_DIGIT_REGEX = /\d/;
const PHONE_ALLOWED_REGEX = /^[0-9+\-() ]+$/;
const DOMAIN_REGEX = /^(?!-)(?:[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}$/;
const CERTIFIER_GROUP_ID_REGEX = /^[0-9a-z]{26}$/i;
const hasReadableWord = (value) =>
  String(value || "")
    .split(/[\s,.;:!?()[\]{}"'/_-]+/)
    .some((token) => /^\p{Letter}{3,}$/u.test(token));

export const validateText = (
  value,
  label,
  { min = 1, max = 500, required = false, meaningful = false } = {},
) => {
  const v = (value ?? "").trim();
  if (!v) {
    return required ? `${label} is required.` : null;
  }
  if (v.length < min) {
    return `${label} must be at least ${min} characters.`;
  }
  if (v.length > max) {
    return `${label} must be at most ${max} characters.`;
  }
  if (!/\p{Letter}/u.test(v)) {
    return `${label} must contain letters, not just numbers or symbols.`;
  }
  if (meaningful && !hasReadableWord(v)) {
    return `${label} must include readable words.`;
  }
  return null;
};

export const validateEmail = (
  value,
  label = "Email",
  { required = false } = {},
) => {
  const v = (value ?? "").trim();
  if (!v) return required ? `${label} is required.` : null;
  if (!EMAIL_REGEX.test(v)) {
    return `Enter a valid ${label.toLowerCase()} (e.g. name@example.com).`;
  }
  return null;
};

export const validateUrl = (
  value,
  label = "URL",
  { required = false, requirePublicHost = false } = {},
) => {
  const v = (value ?? "").trim();
  if (!v) return required ? `${label} is required.` : null;
  if (!URL_REGEX.test(v)) {
    return `${label} must be a valid URL starting with http:// or https://`;
  }
  if (requirePublicHost) {
    try {
      const hostname = new URL(v).hostname.replace(/\.$/, "");
      if (!DOMAIN_REGEX.test(hostname)) {
        return `${label} must use a public domain name.`;
      }
    } catch {
      return `${label} must be a valid URL starting with http:// or https://`;
    }
  }
  return null;
};

// Accepts either a full http(s) URL or a handle starting with "@".
// Rejects numeric-only or symbol-only values.
export const validateSocial = (value, label) => {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (v.startsWith("@")) {
    if (!SOCIAL_HANDLE_REGEX.test(v)) {
      return `${label} handle can only contain letters, numbers, dots, underscores, or hyphens.`;
    }
    return null;
  }
  if (!URL_REGEX.test(v)) {
    return `Enter a valid ${label} URL (https://...) or a handle starting with @.`;
  }
  return null;
};

export const validateLinkedInUrl = (value, { required = false } = {}) => {
  const v = (value ?? "").trim();
  if (!v) return required ? "LinkedIn URL is required." : null;
  if (!/^https?:\/\/(www\.)?linkedin\.com\/.+/i.test(v)) {
    return "Enter a valid LinkedIn URL (e.g. https://linkedin.com/company/...).";
  }
  return null;
};

export const validatePhone = (
  value,
  label = "Phone",
  { required = false } = {},
) => {
  const v = (value ?? "").trim();
  if (!v) return required ? `${label} is required.` : null;
  if (!PHONE_ALLOWED_REGEX.test(v)) {
    return `${label} can only contain digits, spaces, and + - ( ) characters.`;
  }
  if (!PHONE_DIGIT_REGEX.test(v)) {
    return `${label} must contain digits.`;
  }
  const digitCount = (v.match(/\d/g) || []).length;
  if (digitCount < 7) {
    return `${label} must contain at least 7 digits.`;
  }
  return null;
};

// Bare host name: e.g. "training.example.com". Strips http(s)// and trailing /.
export const validateDomain = (
  value,
  label = "Domain",
  { required = false } = {},
) => {
  const stripped = (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");
  if (!stripped) return required ? `${label} is required.` : null;
  if (!DOMAIN_REGEX.test(stripped)) {
    return `${label} must be a valid domain (e.g. training.yourcompany.com).`;
  }
  return null;
};

// URL-friendly slug: lowercase letters, digits, and hyphens (no leading/trailing
// or doubled hyphens).
export const validateSlug = (
  value,
  label = "Slug",
  { required = true, min = 2 } = {},
) => {
  const v = (value ?? "").trim();
  if (!v) return required ? `${label} is required.` : null;
  if (v.length < min) return `${label} must be at least ${min} characters.`;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v)) {
    return `${label} can only contain lowercase letters, numbers, and hyphens.`;
  }
  return null;
};

export const validateCertifierGroupId = (
  value,
  label = "Certifier Group ID",
) => {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (/\s/.test(v)) return `${label} cannot contain spaces.`;
  if (!CERTIFIER_GROUP_ID_REGEX.test(v)) {
    return `${label} must be a 26-character Certifier group ID.`;
  }
  return null;
};
